/**
 * BankInterestVoucher — Printable transaction voucher for Bank Interest.
 *
 * Rendered inside MasterReportPreview (which handles the print modal,
 * @media print isolation, and the Print button).
 */

import type { ReactNode } from 'react';
import type { BankInterestDto } from '@/types/bankInterest.types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.6 }}>
    <span style={{ color: '#6b7280', minWidth: '150px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const colour: Record<string, { bg: string; text: string }> = {
    Draft:          { bg: '#f3f4f6', text: '#374151' },
    Submitted:      { bg: '#dbeafe', text: '#1e40af' },
    PendingApproval:{ bg: '#fef9c3', text: '#854d0e' },
    Approved:       { bg: '#d1fae5', text: '#065f46' },
    Rejected:       { bg: '#fee2e2', text: '#991b1b' },
    Posted:         { bg: '#a7f3d0', text: '#064e3b' },
    Cancelled:      { bg: '#f3f4f6', text: '#6b7280' },
  };
  const c = colour[status] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
    }}>
      {status}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface BankInterestVoucherProps {
  interest:    BankInterestDto;
  companyName: string;
  printedBy?:  string;
}

export const BankInterestVoucher = ({
  interest,
  companyName,
  printedBy,
}: BankInterestVoucherProps): ReactNode => {

  const isIncome = interest.interestType === 'Income';

  const workflowRows = [
    interest.submittedBy ? { role: 'Submitted By', name: interest.submittedBy, date: interest.submittedDate } : null,
    interest.approvedBy  ? { role: 'Approved By',  name: interest.approvedBy,  date: interest.approvedDate  } : null,
    interest.postedBy    ? { role: 'Posted By',     name: interest.postedBy,    date: interest.postedDate    } : null,
  ].filter(Boolean) as { role: string; name: string; date: string | null | undefined }[];

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>

      {/* ── Voucher Header ──────────────────────────────────────────────────── */}
      <div style={{
        borderBottom: '2px solid #1e3a5f',
        paddingBottom: '12px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
              myLedger ONE
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e3a5f', lineHeight: 1.2 }}>
                {interest.isReversal ? 'Reversal Bank Interest' : 'Bank Interest Voucher'}
              </div>
              <span style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: '10px',
                fontSize: '9px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                background: isIncome ? '#d1fae5' : '#fee2e2',
                color:      isIncome ? '#065f46' : '#991b1b',
              }}>
                {isIncome ? 'Income' : 'Expense'}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
              {companyName}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
              {interest.interestNumber}
            </div>
            <div style={{ marginTop: '4px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
              <StatusBadge status={interest.workflowStatus} />
              {interest.isReversal && (
                <span style={{
                  display: 'inline-block', padding: '1px 7px', borderRadius: '10px',
                  fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                  background: '#fef3c7', color: '#92400e',
                }}>
                  Reversal
                </span>
              )}
            </div>
            <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px' }}>
              Printed: {new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {printedBy && ` · ${printedBy}`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Document details ────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 32px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Bank Account
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{interest.bankAccountName}</div>
          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>{interest.bankAccountCode}</div>
          <LabelValue label="Interest Date"   value={fmtDate(interest.interestDate)} />
          <LabelValue label="Posting Date"    value={fmtDate(interest.postingDate)} />
          <LabelValue label="Period"          value={`${interest.financialPeriodCode} / ${interest.financialYearCode}`} />
        </div>
        <div>
          {(interest.incomeExpenseAccountCode || interest.incomeExpenseAccountName) && (
            <LabelValue
              label={isIncome ? 'Income Account' : 'Expense Account'}
              value={
                <span>
                  {interest.incomeExpenseAccountCode && (
                    <span style={{ fontFamily: 'monospace', fontSize: '9px', color: '#2563eb', marginRight: '4px' }}>
                      {interest.incomeExpenseAccountCode}
                    </span>
                  )}
                  {interest.incomeExpenseAccountName}
                </span>
              }
            />
          )}
          {(interest.whtAccountCode || interest.whtAccountName) && (
            <LabelValue
              label="WHT Account"
              value={
                <span>
                  {interest.whtAccountCode && (
                    <span style={{ fontFamily: 'monospace', fontSize: '9px', color: '#2563eb', marginRight: '4px' }}>
                      {interest.whtAccountCode}
                    </span>
                  )}
                  {interest.whtAccountName}
                </span>
              }
            />
          )}
          <LabelValue label="Currency"        value={`${interest.currencyCode} (Rate: ${interest.exchangeRate.toFixed(4)})`} />
          <LabelValue label="Posting Status"  value={interest.postingStatus} />
          {interest.reference && (
            <LabelValue label="Reference"     value={interest.reference} />
          )}
        </div>
      </div>

      {/* Narration */}
      {interest.narration && (
        <div style={{
          marginBottom: '16px',
          padding: '8px 12px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#374151',
        }}>
          <span style={{ color: '#6b7280', fontWeight: 600 }}>Narration: </span>
          {interest.narration}
        </div>
      )}

      {/* ── Amount section ───────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '20px',
      }}>
        <div style={{ width: '280px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '10px' }}>
            <span style={{ color: '#6b7280' }}>Gross Amount:</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtNum(interest.amount)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '10px' }}>
            <span style={{ color: '#6b7280' }}>WHT Rate:</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtNum(interest.whtRate, 2)}%</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '10px' }}>
            <span style={{ color: '#6b7280' }}>WHT Amount:</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtNum(interest.whtAmount)}</span>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '5px 8px',
            marginTop: '4px',
            background: '#1e3a5f',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: 'white',
          }}>
            <span>Net Amount ({interest.currencyCode}):</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtNum(interest.netAmount)}</span>
          </div>
          {interest.exchangeRate !== 1 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>
                <span>Exchange Rate:</span>
                <span style={{ fontFamily: 'monospace' }}>{fmtNum(interest.exchangeRate, 4)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '9px', color: '#6b7280' }}>
                <span>Base Amount:</span>
                <span style={{ fontFamily: 'monospace' }}>{fmtNum(interest.baseAmount)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Workflow Trail ───────────────────────────────────────────────────── */}
      {workflowRows.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Workflow Trail
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {workflowRows.map((row) => (
                <tr key={row.role} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 8px', color: '#374151' }}>{row.role}</td>
                  <td style={{ padding: '4px 8px', color: '#111827', fontWeight: 500 }}>{row.name}</td>
                  <td style={{ padding: '4px 8px', color: '#374151', fontFamily: 'monospace' }}>{fmtDate(row.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Signature blocks ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginTop: '32px',
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
      }}>
        {['Prepared By', 'Reviewed By', 'Approved By'].map((label) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              paddingTop: '4px',
              fontSize: '9px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
